import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  permissions: string[];
  mustChangePassword?: boolean;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string, organizationId?: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Проверяем сохраненный токен при загрузке
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser({
          id: userData.id,
          username: userData.username,
          email: userData.email || '',
          role: userData.role,
          permissions: userData.permissions || [],
          mustChangePassword: userData.mustChangePassword || false,
          firstName: userData.firstName || userData.first_name,
          lastName: userData.lastName || userData.last_name,
          organizationId: userData.organizationId
        });
      } catch (error) {
        console.error('Ошибка при загрузке пользователя из localStorage:', error);
        // Очищаем поврежденные данные
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string, organizationId?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://poll.volgatermy.com' });
      const response = await api.post('/api/auth/login', { username, password, organizationId });
      const { accessToken, user: apiUser } = response.data.data;
      setUser({
        id: apiUser.id,
        username: apiUser.username,
        email: apiUser.email,
        role: apiUser.role,
        permissions: apiUser.permissions || [],
        mustChangePassword: apiUser.mustChangePassword || false,
        firstName: apiUser.firstName || apiUser.first_name,
        lastName: apiUser.lastName || apiUser.last_name,
        organizationId: apiUser.organizationId
      });
      localStorage.setItem('user', JSON.stringify(apiUser));
      localStorage.setItem('token', accessToken);
    } catch (error) {
      console.error('Login error:', error);
      setError('Ошибка авторизации');
      throw error as Error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    error,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
